// Dashboard/SectionAccordion.tsx
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Chip, Collapse, Divider, IconButton, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ReactNode } from "react";

type SectionHeaderProps = {
  title: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
};

const SectionHeader = ({ title, count, expanded, onToggle }: SectionHeaderProps) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.5}
    onClick={onToggle}
    sx={{
      cursor: "pointer",
      userSelect: "none",
      py: 1.5,
      px: 2,
      bgcolor: expanded ? "action.hover" : "transparent",
      transition: (theme) => theme.transitions.create(["background-color"]),
      "&:hover": { bgcolor: "action.hover" },
    }}
  >
    <Typography variant="h4" sx={{ color: "text.secondary" }}>
      {title}
    </Typography>

    <Chip
      label={count}
      size="small"
      sx={(theme) => ({
        fontWeight: 700,
        bgcolor: expanded ? theme.palette.secondary.main : theme.palette.action.selected,
        color: expanded ? theme.palette.primary.contrastText : theme.palette.text.secondary,
        transition: theme.transitions.create(["background-color", "color"]),
      })}
    />

    <Box sx={{ flexGrow: 1 }} />

    <IconButton
      size="small"
      sx={(theme) => ({
        border: "1px solid",
        borderColor: expanded ? "secondary.main" : theme.palette.divider,
        bgcolor: expanded ? alpha(theme.palette.secondary.main, 0.08) : "transparent",
        transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
        transition: theme.transitions.create(["transform", "background-color", "border-color"]),
      })}
    >
      <ExpandMoreIcon fontSize="small" />
    </IconButton>
  </Stack>
);

type SectionAccordionProps = {
  title: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  children: ReactNode;
};

export const SectionAccordion = ({ title, count, expanded, onToggle, children }: SectionAccordionProps) => (
  <Box
    sx={(theme) => ({
      border: "1px solid",
      borderColor: expanded ? alpha(theme.palette.secondary.main, 0.4) : theme.palette.divider,
      borderRadius: 2,
      overflow: "hidden",
      transition: theme.transitions.create(["border-color"]),
    })}
  >
    <SectionHeader title={title} count={count} expanded={expanded} onToggle={onToggle} />
    <Collapse in={expanded} timeout="auto" unmountOnExit>
      <Divider />
      <Box sx={{ p: 2, bgcolor: "action.hover" }}>{children}</Box>
    </Collapse>
  </Box>
);